/**
 * Generates the array of days for a given month, with padding.
 * Each "day" is a Date object or null.
 * @param {Date} date - A date within the target month (e.g., the 1st)
 * @returns {(Date|null)[]}
 */
export const generateCalendarDays = (date: Date): (Date | null)[] => {
  if (!date) return []

  const year = date.getFullYear()
  const month = date.getMonth()

  const firstDayOfMonth = new Date(year, month, 1)
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const firstDayOfWeek = firstDayOfMonth.getDay() // 0 = Sunday, 1 = Monday, etc.

  console.log('firstDayOfWeek', firstDayOfWeek)
  console.log('daysInMonth', daysInMonth)
  console.log('firstDayOfMonth', firstDayOfMonth)

  const days = []

  // 1. Add null padding for days before the 1st
  for (let i = 0; i < firstDayOfWeek; i++) {
    days.push(null)
  }

  // 2. Add Date objects for each day of the month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(new Date(year, month, day))
  }

  // 3. Add null padding for days after the last day
  const remainingCells = 42 - days.length // 6 rows * 7 cols = 42
  for (let i = 0; i < remainingCells; i++) {
    days.push(null)
  }
  // Return only the first 35 cells (5 rows) if 6th row is all null
  const lastWeek = days.slice(35)
  if (lastWeek.every((day) => day === null)) {
    return days.slice(0, 35)
  }

  return days.slice(0, 42) // Max 6 rows
}

/**
 * Formats a Date object into the string "Month Day Year HH:MM AM/PM"
 * @param {Date} date
 * @returns {string}
 */
const formatSingleDate = (date: Date): string => {
  if (!date) return ''
  // We'll keep the hardcoded time from the image for consistency
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Format the date string to display in the text field
 * @param {start: Date, end: Date} range - An object with start and end date
 * @returns {string}
 */
export const formatDisplayDate = (range: {
  start: Date | null
  end: Date | null
}): string => {
  if (range.start && range.end) {
    // Check if start and end are the same day
    const isSameDay = 
      range.start.getDate() === range.end.getDate() &&
      range.start.getMonth() === range.end.getMonth() &&
      range.start.getFullYear() === range.end.getFullYear()
    
    if (isSameDay) {
      // Show single date when start and end are the same
      return formatSingleDate(range.start)
    }
    
    // Ensure start is always before end in the display
    const [startDay, endDay] =
      range.start < range.end
        ? [range.start, range.end]
        : [range.end, range.start]
    return `${formatSingleDate(startDay)} - ${formatSingleDate(endDay)}`
  }
  if (range.start) {
    // Show the start date while user is selecting the end date
    return formatSingleDate(range.start)
  }
  // Placeholder text when no date is selected
  return 'Select a date range'
}

/**
 * Handle the click event for single day
 * @param {Date} day - A date object
 * @param {{start: Date | null, end: Date | null}} range - An object with start and end date
 * @param {(range: {start: Date | null, end: Date | null}) => void} setRange - An event to set range value
 * @param {(isOpen: boolean) => void} setIsOpen - An event to set isOpen value
 * @returns {void}
 */
export const handleDayClick = (
  day: Date,
  range: { start: Date | null; end: Date | null },
  setRange: (range: { start: Date | null; end: Date | null }) => void,
  setIsOpen: (isOpen: boolean) => void
) => {
  if (!day) return // Ignore clicks on empty padding cells

  // Day is now a full Date object

  if (range.start && range.end) {
    // A full range is already selected, so this click starts a new range
    setRange({ start: day, end: null })
  } else if (range.start && !range.end) {
    // Start is selected, and this click will be the end
    if (day < range.start) {
      // User selected an end date *before* the start date, so we swap
      setRange({ start: day, end: range.start })
    } else {
      // Normal selection, end date is after start date
      setRange({ ...range, end: day })
    }
    // Close the calendar popup after a range is selected
    setIsOpen(false)
  } else {
    // This is the first click (no start date yet)
    setRange({ start: day, end: null })
  }
}

/**
 * Checks if two Date objects are on the same day, month, and year.
 * @param {Date} date1
 * @param {Date} date2
 * @returns {boolean}
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
  if (!date1 || !date2) return false
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  )
}
